import { useMemo } from 'react';
import _ from 'lodash';
import { Button } from 'reactstrap';
import cx from 'classnames';

import { usePortfolioEvaluatorContext } from '../../contexts/PortfolioEvaluatorContext';
import SidePanel, { PanelMainGroup, PanelSection } from '../SidePanel';
import { LabeledData } from '../right-menu/menu-components';
import { asQuantityWithUnit } from '../../utils/formatters';
import { useUnits } from '../../contexts/UnitContext';
import { LONG_AREA_UNITS } from '../../constants/units';
import ExportButton from './ExportButton';
import { layerIdForTileset } from '../../utils/tilesets';

const RightMenu = () => {
  const {
    features,
    suitabilityData,
    tilesets,
    selectedTilesetId,
    setSelectedTilesetId,
  } = usePortfolioEvaluatorContext();

  const suitableAcresPerCrop = useMemo(() => {
    if (!suitabilityData) return null;

    const results = {};
    tilesets.forEach((tileset) => {
      const layerId = layerIdForTileset(tileset.id);
      const suitableAcres = _.sum(suitabilityData.map((d) => d[layerId]));
      results[tileset.name] = suitableAcres;
    });

    return results;
  }, [tilesets, suitabilityData]);

  // get acreage across all farms
  const totalAcreage = useMemo(() => {
    return features.reduce((acc, feature) => {
      return acc + feature.properties.area;
    }, 0);
  }, [features]);
  const { displayUnit, transformToUnit = _.identity } = useUnits(
    LONG_AREA_UNITS,
  );

  // sorted by suitable acres
  const tilesetsAndSuitability = useMemo(
    () =>
      tilesets
        .map((t) => {
          const suitableAcres = suitableAcresPerCrop?.[t.name];
          return [t, suitableAcres];
        })
        .sort(([, a], [, b]) => b - a),
    [tilesets, suitableAcresPerCrop],
  );

  return (
    <SidePanel right>
      {features?.length && (
        <PanelMainGroup>
          <PanelSection>
            <h4>Plots</h4>
            <LabeledData
              label="qty"
              formatter={asQuantityWithUnit('plots', 'plot')}
            >
              {features?.length || 0}
            </LabeledData>
            <LabeledData
              label="area"
              formatter={asQuantityWithUnit(
                displayUnit,
                displayUnit.substring(0, displayUnit.length - 1),
              )}
            >
              {transformToUnit(totalAcreage)}
            </LabeledData>
          </PanelSection>
          {suitableAcresPerCrop &&
            Object.keys(suitableAcresPerCrop).length > 0 && (
              <PanelSection className="pb-3">
                <h4>Project Potential</h4>
                <div>
                  {tilesetsAndSuitability.map(
                    ([{ name, displayName }, suitableAcres]) => (
                      <div key={name}>
                        <span>{displayName}: </span>
                        {suitableAcres != null ? (
                          <span className="float-right">
                            {asQuantityWithUnit('acres', 'acre')(suitableAcres)}
                          </span>
                        ) : (
                          <span className="float-right text-muted">
                            (not calculated)
                          </span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </PanelSection>
            )}
          <PanelSection>
            <h4>Export</h4>
            <ExportButton />
          </PanelSection>
          {tilesets.length > 0 && (
            <PanelSection>
              <h4>View suitability on map</h4>
              <div>
                {tilesets
                  .concat([{ id: null, displayName: 'None' }])
                  .map(({ id, displayName }) => (
                    <Button
                      key={`${id}`}
                      size="sm"
                      color="text"
                      className={cx('d-block w-100 text-left p-0 pb-1', {
                        'text-muted': id !== selectedTilesetId,
                      })}
                      onClick={() => setSelectedTilesetId(id)}
                    >
                      {displayName}
                    </Button>
                  ))}
              </div>
            </PanelSection>
          )}
        </PanelMainGroup>
      )}
    </SidePanel>
  );
};

export default RightMenu;
